import { useState } from 'react'
import { Menu } from 'lucide-react'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'
import { SidebarContent } from '@/components/Sidebar'
import { useAuth } from '@/context/AuthContext'
import AccountSettings from './components/AccountSettings'
import SecuritySettings from './components/SecuritySettings'
import { getUserDisplayName, getUserInitials } from '@/utils/user-display'
import { Button } from '@/components/ui/button'

type TabType = 'account' | 'security'

export default function SettingsPage() {
  const [activeTab, setActiveTab] = useState<TabType>('account')
  const { user } = useAuth()
  const displayName = getUserDisplayName(user)
  const initials = getUserInitials(user)
  return (
    <div className='min-h-screen w-full bg-[#EDEFF3]'>
      <div className='p-4 sm:p-[27px]'>
        <div className='bg-white rounded-[24px] min-h-[calc(100vh-54px)]'>
          {/* Header */}
          <div className='px-4 sm:px-6 lg:px-8 py-4 sm:py-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4'>
            <div className='flex items-center gap-4'>
              <Sheet>
                <SheetTrigger asChild>
                  <Button variant='ghost' size='icon' className='lg:hidden -ml-2'>
                    <Menu className='h-6 w-6' />
                    <span className='sr-only'>Toggle menu</span>
                  </Button>
                </SheetTrigger>
                <SheetContent side='left' className='w-[280px] p-0'>
                  <SidebarContent organizationName={user?.coupleOrganizationName || 'IRIS Dashboard'} />
                </SheetContent>
              </Sheet>
              <h1 className='text-[28px] sm:text-[36px] lg:text-[40px] font-bold text-[#151515] leading-[1.4] sm:leading-[70px]'>Settings</h1>
            </div>
            <div className='flex items-center gap-4 lg:gap-8'>
              <div className='flex items-center gap-3'>
                <div className='w-[48px] h-[47px] bg-[#434343] rounded-full flex items-center justify-center'>
                  <span className='text-[16px] font-bold text-white'>{initials}</span>
                </div>
                <span className='text-[24px] font-bold text-[#353535] hidden sm:block'>{displayName}</span>
              </div>
              <div className='w-[41px] h-[39px] bg-[#F7F8FA] rounded-full flex items-center justify-center cursor-pointer'>
                <svg width='4' height='18' viewBox='0 0 4 18' fill='#434343'>
                  <circle cx='2' cy='2' r='2' />
                  <circle cx='2' cy='9' r='2' />
                  <circle cx='2' cy='16' r='2' />
                </svg>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className='px-4 sm:px-6 lg:px-8 pb-6 lg:pb-8'>
            {/* Tabs */}
            <div className='flex gap-2 mb-8'>
              <button
                onClick={() => setActiveTab('account')}
                className={`px-6 py-2.5 rounded-[8px] font-semibold text-[14px] transition-colors ${
                  activeTab === 'account'
                    ? 'bg-[#151515] text-white'
                    : 'bg-white text-[#151515] border border-[#D0D5DD] hover:bg-gray-50'
                }`}
              >
                Account Settings
              </button>
              <button
                onClick={() => setActiveTab('security')}
                className={`px-6 py-2.5 rounded-[8px] font-semibold text-[14px] transition-colors ${
                  activeTab === 'security'
                    ? 'bg-[#151515] text-white'
                    : 'bg-white text-[#151515] border border-[#D0D5DD] hover:bg-gray-50'
                }`}
              >
                Security
              </button>
            </div>

            {/* Tab Content */}
            {activeTab === 'account' ? <AccountSettings /> : <SecuritySettings />}
          </div>
        </div>
      </div>
    </div>
  )
}
